/* eslint-disable @typescript-eslint/no-explicit-any */
//* eslint-disable @typescript-eslint/no-explicit-any */
import { useAuthStore } from "@/stores/authStore";
import { useSidebarStore } from "@/stores/SidebarStore";
import { useTitleStore, useActionsStore } from "@/stores/titleStore";
import { Link, useNavigate } from "react-router";
import { useEffect, useRef, useState, useCallback } from "react";
import { signalRService } from "@/services/signalRService";
import { isSignalRAllowed, onSignalRAllowed } from "@/lib/signalRGate";
import { notifications, type NotificationDto } from "@/services/apiService"; // Cambiado aquí
import { PermissionElement } from "./permission-element";

export function Navbar() {
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const title = useTitleStore((state) => state.title);
  const actions = useActionsStore((state) => state.actions);
  const navigate = useNavigate();

  // Estados del sidebar
  const { isOpen, toggleSidebar } = useSidebarStore();

  // Detección móvil simplificada
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLegacy, setIsLegacy] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Detect legacy browser features
  useEffect(() => {
    try {
      const supports = (prop: string, value?: string) => {
        try {
          const css = CSS as unknown as {
            supports?: (conditionText: string) => boolean;
          };
          if (typeof css?.supports === "function") {
            return css.supports(`${prop}: ${value ?? ""}`);
          }
        } catch {
          /* ignore */
        }
        return false;
      };
      const hasTransform = supports("transform", "translateX(-50%)");
      const hasFlexbox = supports("display", "flex");
      const ua = navigator.userAgent || "";
      const veryOldUA = /MSIE\s|Trident\/|Edge\/(12|13|14)/.test(ua);
      setIsLegacy(!(hasTransform && hasFlexbox) || veryOldUA);
    } catch {
      setIsLegacy(true);
    }
  }, []);

  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationList, setNotificationList] = useState<NotificationDto[]>(
    []
  );
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [showAllNotificationsModal, setShowAllNotificationsModal] =
    useState(false);
  const [allNotificationsList, setAllNotificationsList] = useState<
    NotificationDto[]
  >([]);
  const [isLoadingAllNotifications, setIsLoadingAllNotifications] =
    useState(false);

  const notifRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  // Cargar notificaciones iniciales
  const loadNotifications = useCallback(async () => {
    setIsLoadingNotifications(true);
    try {
      const response = await notifications.getNotifications(true, 20);
      if (response.status?.success && response.data) {
        setNotificationList(response.data);
      }
    } catch (error) {
      console.error("Error al cargar notificaciones:", error);
    } finally {
      setIsLoadingNotifications(false);
    }
  }, []);

  // Cargar todas las notificaciones (incluidas las leídas)
  const loadAllNotifications = useCallback(async () => {
    setIsLoadingAllNotifications(true);
    try {
      const response = await notifications.getNotifications(false, 1000);
      if (response.status?.success && response.data) {
        setAllNotificationsList(response.data);
      }
    } catch (error) {
      console.error("Error al cargar todas las notificaciones:", error);
    } finally {
      setIsLoadingAllNotifications(false);
    }
  }, []);

  // Cargar contador de no leídas
  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await notifications.getUnreadCount();
      if (response.status?.success && response.data) {
        setUnreadCount(response.data.unreadCount);
      }
    } catch (error) {
      console.error("Error al cargar contador:", error);
    }
  }, []);

  // Manejar eventos del hub
  const handleHubEvent = useCallback(
    (event: string, data: any) => {
      switch (event) {
        case "new": {
          const newNotification: NotificationDto = {
            idNotificacion: data.id,
            titulo: data.title,
            mensaje: data.message,
            icono: data.icon,
            colorFondo: data.bgColor,
            colorIcono: data.iconColor,
            orden: data.order || 0,
            prioridad: data.priority || 0,
            esPush: data.isPush || false,
            href: data.href,
            leido: false,
            fecha: new Date(),
            tiempoRelativo: data.time || "Ahora",
            tipoEnvio: data.tipoEnvio || "Usuario",
          };

          setNotificationList((prev) => {
            // Si ya existe en la lista, no la volvemos a añadir
            if (
              prev.some(
                (n) => n.idNotificacion === newNotification.idNotificacion
              )
            ) {
              return prev;
            }
            return [newNotification, ...prev];
          });

          setUnreadCount((prev) => prev + 1);

          if (
            data.isPush &&
            "Notification" in window &&
            Notification.permission === "granted"
          ) {
            new Notification(data.title, {
              body: data.message,
              icon: "/logo.png",
              tag: `notification-${data.id}`,
            });
          }
          break;
        }

        case "read":
          setNotificationList((prev) =>
            prev.map((n) =>
              n.idNotificacion === data ? { ...n, leido: true } : n
            )
          );
          setUnreadCount((prev) => Math.max(0, prev - 1));
          break;

        case "allRead":
          setNotificationList((prev) =>
            prev.map((n) => ({ ...n, leido: true }))
          );
          setUnreadCount(0);
          break;

        case "unreadCount":
          setUnreadCount(data);
          break;

        case "connected":
          loadNotifications();
          loadUnreadCount();
          break;
        // ...
      }
    },
    [loadNotifications, loadUnreadCount]
  );

  // Conectar al hub al montar
  useEffect(() => {
    if (!(token && user)) return;
    let unsub: (() => void) | null = null;
    const start = () => {
      // Conectar al servicio de notificaciones
      signalRService.connect(token);
      // Suscribirse a eventos
      unsub = signalRService.subscribe(handleHubEvent);
      // Cargar datos iniciales
      loadNotifications();
      loadUnreadCount();
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission();
      }
    };
    if (isSignalRAllowed()) {
      start();
    } else {
      onSignalRAllowed(start);
    }
    return () => {
      if (unsub) unsub();
    };
  }, [token, user, handleHubEvent, loadNotifications, loadUnreadCount]);

  // Marcar notificación como leída
  const markAsRead = async (notification: NotificationDto) => {
    if (!notification.leido) {
      try {
        const response = await notifications.markAsRead(
          notification.idNotificacion
        );
        if (response.status?.success) {
          // El hub actualizará el estado automáticamente
          await signalRService.markAsRead(notification.idNotificacion);
        }
      } catch (error) {
        console.error("Error al marcar como leída:", error);
      }
    }
  };

  // Marcar todas como leídas
  const markAllAsRead = async () => {
    try {
      const response = await notifications.markAllAsRead();
      if (response.status?.success) {
        // El hub actualizará el estado automáticamente
      }
    } catch (error) {
      console.error("Error al marcar todas como leídas:", error);
    }
  };

  // Navegar al hacer click en notificación
  const handleNotificationClick = (notification: NotificationDto) => {
    markAsRead(notification);
    if (notification.href) {
      navigate(notification.href);
      setNotifOpen(false);
    }
  };

  // Manejar clicks fuera de los menús
  const handleClickOutside = (event: MouseEvent) => {
    if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
      setNotifOpen(false);
    }
    if (userRef.current && !userRef.current.contains(event.target as Node)) {
      setUserMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleClickCerrarSesion = async () => {
    signalRService.isIntentionalDisconnect = false; // Marcar como desconexión intencional
    await signalRService.disconnect(true);
    useAuthStore.getState().logout();
    navigate("/login");
  };

  // Obtener color de prioridad
  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case 3:
        return "bg-red-500 text-white";
      case 2:
        return "bg-orange-500 text-white";
      case 1:
        return "bg-yellow-500 text-white";
      default:
        return "";
    }
  };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 3:
        return "Urgente";
      case 2:
        return "Alta";
      case 1:
        return "Media";
      default:
        return "";
    }
  };

  return (
    <>
      <style>{`
                /* Efectos para modo oscuro por defecto */
                .dark-glass-effect {
                    background: rgba(55, 65, 81, 0.95);
                    backdrop-filter: blur(8px);
                    border: 1px solid #475569;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
                }
                
                .notification-glass-effect {
                    background: rgba(31, 41, 55, 0.95);
                    backdrop-filter: blur(12px);
                    border: 1px solid #374151;
                    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
                }
                
                /* Efectos para modo claro */
                @media (prefers-color-scheme: light) {
                    .dark-glass-effect {
                        background: rgba(255, 255, 255, 0.95);
                        backdrop-filter: blur(8px);
                        border: 1px solid #e5e7eb;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                        color: #111827;
                    }
                    
                    .notification-glass-effect {
                        background: rgba(249, 250, 251, 0.95);
                        backdrop-filter: blur(12px);
                        border: 1px solid #d1d5db;
                        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
                        color: #111827;
                    }
                }
                
                /* Legacy fallbacks - fondo blanco para navegadores antiguos */
                .legacy .dark-glass-effect {
                    background: #fefefe !important;
                    border: 1px solid #dee2e6 !important;
                    color: #212529 !important;
                }
                .legacy .dark-glass-effect .text-white { color: #212529 !important; }
                .legacy .dark-glass-effect .text-gray-400 { color: #495057 !important; }
                .legacy .dark-glass-effect .text-gray-200 { color: #495057 !important; }
                .legacy .dark-glass-effect .text-gray-300 { color: #6c757d !important; }
                .legacy .dark-glass-effect .text-blue-400 { color: #0d6efd !important; }
                
                .legacy .notification-glass-effect {
                    background: #fefefe !important;
                    border: 1px solid #dee2e6 !important;
                    color: #212529 !important;
                }
                .legacy .notification-glass-effect .text-white { color: #212529 !important; }
                .legacy .notification-glass-effect .text-gray-400 { color: #495057 !important; }
                .legacy .notification-glass-effect .text-gray-200 { color: #495057 !important; }
                .legacy .notification-glass-effect .text-gray-300 { color: #6c757d !important; }
                .legacy .notification-glass-effect .text-blue-400 { color: #0d6efd !important; }
                .legacy .notification-glass-effect .hover\\:bg-gray-700:hover { background-color: #e9ecef !important; }
                
                /* Scrollbar simplificado */
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #666;
                    border-radius: 3px;
                }
                
                /* Indicador de conexión simplificado */
                .connection-indicator {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background-color: #10b981;
                    position: absolute;
                    bottom: -2px;
                    right: -2px;
                    border: 2px solid #1e1e28;
                }
                
                .connection-indicator.disconnected {
                    background-color: #ef4444;
                }
                
                /* Soporte para alto contraste */
                @media (prefers-contrast: high) {
                    .dark-glass-effect {
                        background: #ffffff !important;
                        border: 2px solid #000000 !important;
                    }
                    
                    .notification-glass-effect {
                        background: #ffffff !important;
                        border: 2px solid #000000 !important;
                    }
                    
                    .text-white { color: #000000 !important; }
                    .text-gray-400 { color: #000000 !important; }
                    .text-blue-400 { color: #0078d4 !important; }
                }
            `}</style>

      <nav className="sticky top-0 z-50 border-b border-gray-700 bg-slate-800">
        <div className="bg-slate-700 border-b border-gray-600">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              {/* Lado izquierdo - Menú hamburguesa + Logo/Página actual */}
              <div className="flex items-center">
                {/* Botón menú hamburguesa - Solo en móvil */}
                {isMobile && (
                  <button
                    onClick={toggleSidebar}
                    className="p-2 mr-3 text-gray-400 hover:text-white hover:bg-gray-700 rounded-xl transition-colors"
                    aria-label="Toggle sidebar"
                  >
                    <svg
                      className={`h-6 w-6 transition-transform duration-200 ${
                        isOpen ? "rotate-90" : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      {isOpen ? (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      ) : (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4 6h16M4 12h16M4 18h16"
                        />
                      )}
                    </svg>
                  </button>
                )}

                {/* Título de la página */}
                <div className="text-lg font-semibold text-white/90 tracking-tight">
                  {title}
                </div>
              </div>

              {/* Acciones - Lado derecho */}
              <div className="flex items-center gap-1">
                {/* Botones personalizados */}
                {actions && (
                  <div className="flex items-center gap-1 mr-2">{actions}</div>
                )}

                {/* Notificaciones */}
                <div className="relative" ref={notifRef}>
                  <button
                    onClick={() => setNotifOpen(!notifOpen)}
                    className="relative p-2.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded-xl transition-colors"
                    aria-label="Notificaciones"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    </svg>
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium shadow-lg">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                    <div
                      className={`connection-indicator ${
                        signalRService.isConnected ? "" : "disconnected"
                      }`}
                    ></div>
                  </button>

                  {notifOpen && (
                    <div className={isLegacy ? "legacy" : ""}>
                      <div
                        className="
                                          absolute
                                          z-50
                                          mt-2
                                          notification-glass-effect
                                          rounded-2xl
                                          overflow-hidden
                                          -left-10
                                          -translate-x-1/2
                                          w-[calc(100vw-1rem)]
                                          sm:left-auto
                                          sm:translate-x-0
                                          sm:right-0
                                          sm:w-96
                                          sm:max-w-[24rem]
                                        "
                      >
                        <div className="p-4 border-b border-gray-600 flex items-center justify-between">
                          <h3 className="font-semibold text-white text-base">
                            Notificaciones
                          </h3>
                          {unreadCount > 0 && (
                            <button
                              onClick={markAllAsRead}
                              className="text-xs text-blue-400 hover:text-blue-300 font-medium transition-colors"
                            >
                              Marcar todas como leídas
                            </button>
                          )}
                        </div>

                        <div className="max-h-96 overflow-y-auto custom-scrollbar">
                          {isLoadingNotifications ? (
                            <div className="p-8 text-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
                              <p className="text-gray-400 text-sm mt-2">
                                Cargando notificaciones...
                              </p>
                            </div>
                          ) : notificationList.length > 0 ? (
                            <div className="p-2 space-y-1">
                              {notificationList
                                .slice(0, 10)
                                .map((notification) => (
                                  <div
                                    key={notification.idNotificacion}
                                    className={`group hover:bg-gray-700 p-3 rounded-xl transition-colors cursor-pointer relative ${
                                      !notification.leido ? "bg-gray-700" : ""
                                    }`}
                                    onClick={() =>
                                      handleNotificationClick(notification)
                                    }
                                  >
                                    <div className="flex items-start space-x-3">
                                      <div
                                        className={`${notification.colorFondo} p-2 rounded-lg flex-shrink-0`}
                                      >
                                        <span
                                          className={notification.colorIcono}
                                        >
                                          {notification.icono}
                                        </span>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center space-x-2">
                                            <p className="text-sm font-medium text-white/90">
                                              {notification.titulo}
                                            </p>
                                            {!notification.leido && (
                                              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                                            )}
                                          </div>
                                          {notification.prioridad > 0 && (
                                            <span
                                              className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(
                                                notification.prioridad
                                              )}`}
                                            >
                                              {getPriorityLabel(
                                                notification.prioridad
                                              )}
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-300 mt-1 line-clamp-2">
                                          {notification.mensaje}
                                        </p>
                                        <div className="flex items-center justify-between mt-2">
                                          <p className="text-xs text-gray-400">
                                            {notification.tiempoRelativo}
                                          </p>
                                          {notification.tipoEnvio && (
                                            <span className="text-xs text-gray-500">
                                              {notification.tipoEnvio}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    {notification.href && (
                                      <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <svg
                                          className="w-4 h-4 text-gray-400"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M9 5l7 7-7 7"
                                          />
                                        </svg>
                                      </div>
                                    )}
                                  </div>
                                ))}
                            </div>
                          ) : (
                            <div className="p-8 text-center">
                              <div className="w-16 h-16 mx-auto mb-4 bg-gray-700 rounded-full flex items-center justify-center">
                                <svg
                                  className="w-8 h-8 text-gray-500"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                                  />
                                </svg>
                              </div>
                              <p className="text-gray-400 text-sm">
                                No tienes notificaciones
                              </p>
                            </div>
                          )}
                        </div>

                        {/* {notificationList.length > 0 && (
                                                
                                            )} */}
                        <div className="p-4 border-t border-gray-600 bg-gray-700">
                          <button
                            onClick={() => {
                              setShowAllNotificationsModal(true);
                              setNotifOpen(false);
                              loadAllNotifications();
                            }}
                            className="w-full text-center text-blue-400 text-sm font-medium hover:text-blue-300 transition-colors"
                          >
                            Ver todas las notificaciones
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Usuario */}
                <div className="relative" ref={userRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-3 text-sm bg-gray-700 hover:bg-gray-600 px-3 py-2 rounded-xl transition-colors border border-gray-600"
                  >
                    <img
                      className="w-8 h-8 rounded-lg"
                      alt="Avatar"
                      src={`https://ui-avatars.com/api/?name=${user?.nombre_usuario}&background=0D8ABC&color=fff&rounded=true`}
                    />
                    <div className="text-left hidden sm:block">
                      <p className="font-medium text-white/90">
                        {user?.nombre}
                      </p>
                      <p className="text-xs text-gray-300">
                        @{user?.nombre_usuario}
                      </p>
                    </div>
                    <svg
                      className="w-4 h-4 text-gray-400 ml-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </button>

                  {userMenuOpen && (
                    <div className={isLegacy ? "legacy" : ""}>
                      <div className="absolute right-0 mt-2 w-64 dark-glass-effect rounded-2xl z-50 overflow-hidden">
                        <div className="p-4 border-b border-gray-600">
                          <div className="flex items-center space-x-3">
                            <img
                              className="w-12 h-12 rounded-xl"
                              alt="Avatar"
                              src={`https://ui-avatars.com/api/?name=${user?.nombre_usuario}&background=0D8ABC&color=fff&rounded=true`}
                            />
                            <div className="flex-1">
                              <p className="font-medium text-white">
                                {user?.nombre}
                              </p>
                              <p className="text-sm text-gray-400">
                                @{user?.nombre_usuario}
                              </p>
                              {user?.roles && (
                                <div className="mt-1">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-500/20 text-blue-300">
                                    {user.roles
                                      .map((role) => role.nombre)
                                      .join(", ")}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="py-2">
                          <a
                            href="#"
                            className="flex items-center px-4 py-3 text-sm text-gray-200 hover:bg-gray-700 hover:text-white transition-colors group"
                          >
                            <svg
                              className="w-5 h-5 mr-3 text-gray-400 group-hover:text-gray-300"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              ></path>
                            </svg>
                            Mi Perfil
                          </a>

                          <PermissionElement
                            requiredPermission="puede_ver_sistemas_panel_administracion"
                            children={
                              <>
                                <Link
                                  to="/settings"
                                  className="flex items-center px-4 py-3 text-sm text-gray-200 hover:bg-gray-700 hover:text-white transition-colors group"
                                >
                                  <svg
                                    className="w-5 h-5 mr-3 text-gray-400 group-hover:text-gray-300"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                    ></path>
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    ></path>
                                  </svg>
                                  Configuración
                                </Link>
                                <Link
                                  to="/settings/panel-administracion"
                                  className="flex items-center px-4 py-3 text-sm text-gray-200 hover:bg-gray-700 hover:text-white transition-colors group"
                                >
                                  <svg
                                    className="w-5 h-5 mr-3 text-gray-400 group-hover:text-gray-300"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                                    ></path>
                                  </svg>
                                  Panel de Admin
                                </Link>
                              </>
                            }
                          />
                        </div>

                        <div className="border-t border-gray-600">
                          <button
                            onClick={handleClickCerrarSesion}
                            className="flex items-center w-full px-4 py-3 text-sm text-red-400 hover:bg-red-900 hover:text-red-300 transition-colors group"
                          >
                            <svg
                              className="w-5 h-5 mr-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                              ></path>
                            </svg>
                            Cerrar sesión
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Modal de todas las notificaciones */}
      {showAllNotificationsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowAllNotificationsModal(false)}
          ></div>

          {/* Modal */}
          <div className={isLegacy ? "legacy" : ""}>
            <div className="relative w-full max-w-4xl mx-4 max-h-[90vh] notification-glass-effect rounded-2xl overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b border-gray-600 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">
                  Todas las Notificaciones
                </h2>
                <div className="flex items-center gap-4">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors"
                    >
                      Marcar todas como leídas
                    </button>
                  )}
                  <button
                    aria-label="Cerrar modal"
                    onClick={() => setShowAllNotificationsModal(false)}
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="max-h-[70vh] overflow-y-auto custom-scrollbar">
                {isLoadingAllNotifications ? (
                  <div className="p-12 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
                    <p className="text-gray-400 text-sm mt-4">
                      Cargando todas las notificaciones...
                    </p>
                  </div>
                ) : allNotificationsList.length > 0 ? (
                  <div className="p-4 space-y-2">
                    {allNotificationsList.map((notification) => (
                      <div
                        key={notification.idNotificacion}
                        className={`group hover:bg-gray-700 p-4 rounded-xl transition-colors cursor-pointer border border-gray-600 ${
                          !notification.leido
                            ? "bg-gray-700 border-gray-500"
                            : "hover:border-gray-500"
                        }`}
                        onClick={() => {
                          handleNotificationClick(notification);
                          setShowAllNotificationsModal(false);
                        }}
                      >
                        <div className="flex items-start space-x-4">
                          <div
                            className={`${notification.colorFondo} p-3 rounded-lg flex-shrink-0`}
                          >
                            <span className={notification.colorIcono}>
                              {notification.icono}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-3">
                                <h3 className="text-base font-medium text-white/90">
                                  {notification.titulo}
                                </h3>
                                {!notification.leido && (
                                  <div className="w-2.5 h-2.5 bg-blue-400 rounded-full"></div>
                                )}
                              </div>
                              {notification.prioridad > 0 && (
                                <span
                                  className={`text-xs px-3 py-1 rounded-full font-medium ${getPriorityColor(
                                    notification.prioridad
                                  )}`}
                                >
                                  {getPriorityLabel(notification.prioridad)}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-300 mb-3 leading-relaxed">
                              {notification.mensaje}
                            </p>
                            <div className="flex items-center justify-between text-xs text-gray-400">
                              <span>{notification.tiempoRelativo}</span>
                              {notification.tipoEnvio && (
                                <span className="bg-gray-700 px-2 py-1 rounded-md">
                                  {notification.tipoEnvio}
                                </span>
                              )}
                            </div>
                          </div>
                          {notification.href && (
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                              <svg
                                className="w-5 h-5 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <div className="w-20 h-20 mx-auto mb-6 bg-gray-700 rounded-full flex items-center justify-center">
                      <svg
                        className="w-10 h-10 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                        />
                      </svg>
                    </div>
                    <p className="text-gray-400">No tienes notificaciones</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
